import {
  PermissionCategory,
  PermissionLevel,
  VIEW_ONLY_CATEGORIES,
} from '@/client/custom-roles';
import { FormikSegmentedControl } from '@/components/form/FormikSegmentedControl';
import { Field } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type PermissionRowProps = {
  category: PermissionCategory;
};

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */
  padding: var(--default-padding-small);
  border: 1px solid var(--gray-border-lightest);
  border-radius: var(--xsmall-border-radius);
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding); /* Figma 3px -> --xxsmall-padding (4px) */
`;

const Title = styled.span`
  font-size: var(--small-font-size);
  font-weight: var(--semibold-font-weight);
  line-height: var(--small-line-height-19);
`;

const Description = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--default-line-height);
`;

export const PermissionRow: React.FC<PermissionRowProps> = ({ category }) => {
  const { t } = useTranslation();

  const levels = VIEW_ONLY_CATEGORIES.includes(category)
    ? [PermissionLevel.NONE, PermissionLevel.VIEW]
    : [PermissionLevel.NONE, PermissionLevel.VIEW, PermissionLevel.EDIT];

  return (
    <Row>
      <TextBlock>
        <Title>{t(`account.rolesAccess.category.${category}.title`)}</Title>
        <Description>
          {t(`account.rolesAccess.category.${category}.description`)}
        </Description>
      </TextBlock>

      <Field
        name={`permissions.${category}`}
        component={FormikSegmentedControl}
        ariaLabel={t(`account.rolesAccess.category.${category}.title`)}
        options={levels.map((level) => ({
          label: t(`account.rolesAccess.level.${level}`),
          value: level,
        }))}
      />
    </Row>
  );
};
